<template>
  <div class="container-login">
    <div class="container-login-layout login-left">
      <div class="container-login-contents">
        <LoginCarousel />
      </div>
    </div>

    <div class="container-login-layout login-right">
      <div class="container-login-detail">
        <Head class="move-up" />
        <div class="container-login-contents fade-in">
          <h2 class="medium" v-html="t('start_ai_realtime_interpretation')"></h2>
          <!-- <button type="button" class="button-text language-wrapper" @click="toggleLangModal">
            <span v-html="t('login_language_to_interpret')" />
          </button> -->

          <!--소셜미디어로그인-->
          <div v-show="!isSub" class="wrapper-login-forms">
            <button type="button" class="line-btn l-btn button-full" @click="onClickLoginGoogleCustom">
              <img :src="getAssetImageUrl('common/icons/google-logo.svg')" alt="logo" class="icon-small" />
              {{ t('continue_with_social', { social: 'Google' }) }}
            </button>

            <button
              id="profile-facebook"
              ref="refFacebookButton"
              type="button"
              class="line-btn l-btn button-full"
              @click="onClickLoginFacebook"
            >
              <img :src="getAssetImageUrl('common/icons/facebook-logo.svg')" alt="logo" class="icon-small" />
              {{ t('continue_with_social', { social: 'Facebook' }) }}
            </button>
          </div>

          <!--서브계정로그인-->
          <div v-if="isSub" class="wrapper-login-forms">
            <FormLogin v-if="isSub" v-model:login-form="inputFormLogin" domain="SELF_SERVICE" :error-login />
            <ButtonProcess full-width :disabled="isProcessingLogin" @click="handleLoginSub">
              {{ t('login') }}
            </ButtonProcess>
          </div>

          <!--하위Action버튼들-->
          <div class="wrapper-login-forms">
            <button class="gray-btn l-btn button-full" @click="isSub ? setLoginTypeHost() : setLoginTypeSub()">
              {{ isSub ? t('ss_login_as_host') : t('ss_login_as_sub') }}
            </button>
            <a class="link center" :href="supportUrl">
              {{ t('ss_login_have_issues') }}
            </a>
          </div>
        </div>
      </div>

      <!--약관-->
      <div class="wrapper-policy fade-in">
        <a
          class="link-policy link-smaller link-dimmer"
          rel="noopener noreferrer"
          @click="openNewTab('ss-policy', { id: 'service' })"
        >
          {{ t('policy_service') }}
        </a>
        <a
          class="link-policy link-smaller link-dimmer"
          rel="noopener noreferrer"
          @click="openNewTab('ss-policy', { id: 'privacy' })"
        >
          {{ t('policy_privacy') }}
        </a>
        <a class="link-policy link-smaller link-dimmer" @click="openNewTab('ss-policy', { id: 'info' })">
          {{ t('policy_info') }}
        </a>
      </div>
    </div>

    <teleport to="body">
      <ModalSelectLanguage
        v-if="showLangModal"
        :current-language="getLanguageByCode(userStore.pageLanguage)"
        :subtitle-key="t('label_choose_display_language')"
        @close="toggleLangModal"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { loginSelfService, loginSelfServiceGoogle, requestAuthKey, requestLoginApp } from '@/api/apiService';
import FormLogin from '@/components/domains/chat/common/FormLogin.vue';
import Head from '@/components/domains/chat/common/Head.vue';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import LoginCarousel from '@/components/domains/login/LoginCarousel.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import { getLanguageByCode } from '@/static/languages';
import { useAdminStore } from '@/stores/admin';
import { useModalStore } from '@/stores/modal';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { TypeLoginChannel } from '@/types/common';
import { IErrorLogin, ILoginForm } from '@/types/components';
import { IAppError } from '@/types/response';
import { isEmbeddedBrowser } from '@/utils/device';
import { encryptPassword } from '@/utils/encryptor';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const userStore = useUserStore();
const adminStore = useAdminStore();
const toastStore = useToastStore();
const modalStore = useModalStore();

const refFacebookButton = ref<HTMLButtonElement | null>(null); // 페이스북 로그인 버튼
const showLangModal = ref<boolean>(false);
const isSub = ref<boolean>(!!route.query.partnerCode); // 사용자가 SUB 인지 여부
const inputFormLogin = ref<ILoginForm>({
  partnerCode: '',
  userId: '',
  password: '',
  loginType: 'CUSTOMER',
});
const errorLogin = ref<IErrorLogin>();
const isProcessingLogin = ref<boolean>(false);

// window.FB / window.google 를 쓸 수 있도록 해주는 타입 보완
declare global {
  interface Window {
    google?: any;
    fbAsyncInit?: () => void;
    FB?: any;
  }
}

const supportUrl = computed<string>(() => {
  const base = 'https://www.sconai.com/sconchat/home';
  const lang = userStore.pageLanguage;

  return lang ? `${base}?lang=${lang}#support` : `${base}#support`;
});

// 페이스북 SDK 초기화
const initFacebook = () => {
  // 1) Define fbAsyncInit BEFORE loading sdk.js
  window.fbAsyncInit = function () {
    window.FB.init({
      appId: '1742116097180481',
      xfbml: true,
      version: 'v23.0',
    });

    window.FB.getLoginStatus(function (response) {
      console.debug('is user logged in via FB?', response);
    });
  };

  // 2) Load Facebook SDK script ONLY ONCE
  if (!document.getElementById('facebook-jssdk')) {
    const js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.async = true;
    js.defer = true;
    document.body.appendChild(js);
  }
};

// 커스텀 구글 버튼 클릭 시
const onClickLoginGoogleCustom = () => {
  if (isEmbeddedBrowser()) {
    modalStore.openModal({ modalKey: 'LoginGuideOpenBrowser' });
    return;
  }

  const urlOrigin = window.location.origin;

  const params = new URLSearchParams({
    client_id: '60715395606-cmqq27tih5875l6q4ce5j8ajgpmd4lq7.apps.googleusercontent.com',
    redirect_uri: `${urlOrigin}/callback.html`,
    response_type: 'code',
    scope: 'openid email profile',
    prompt: 'select_account',
  });

  const popupGoogle = window.open(
    `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
    'google-login',
    'width=500,height=600,left=400,top=200'
  );

  if (!popupGoogle) {
    toastStore.error(t('error_popup_blocked'));
    return;
  }

  const handleMessagePopup = async (event) => {
    if (event.origin !== urlOrigin) return;
    if (!event.data?.code) return;

    clearInterval(intervalCheckPopupClosed);
    window.removeEventListener('message', handleMessagePopup);
    await handleLoginGoogle(event.data.code);
  };
  window.addEventListener('message', handleMessagePopup);

  const intervalCheckPopupClosed = setInterval(() => {
    if (popupGoogle?.closed) {
      clearInterval(intervalCheckPopupClosed);
      window.removeEventListener('message', handleMessagePopup);
    }
  }, 500);
};

// 언어선택 모달 토글
const toggleLangModal = () => {
  showLangModal.value = !showLangModal.value;
};

const openNewTab = (name: string, query: Record<string, string>) => {
  const newUrl = router.resolve({ name, query }).href;
  window.open(newUrl, '_blank');
};

// 구글 로그인 시도 처리
const handleLoginGoogle = async (code) => {
  try {
    const resLogin = await loginSelfServiceGoogle('THIRD_PARTY_GOOGLE', code);
    if (!resLogin.success) {
      switch (resLogin.errorCode) {
        case 1004:
          goToSignup('google', '', resLogin.errorMessage);
          break;
        default:
          console.error('FAIL');
      }
      return;
    } else {
      handleLoginSuccess(resLogin.data);
    }
  } catch (err) {
    console.error('구글 로그인이 실패했습니다.', err);
  }
};

// 구글 로그인 버튼 클릭 시
const onClickLoginFacebook = () => {
  if (!window.FB) {
    console.warn('Facebook SDK not loaded yet');
    return;
  }

  window.FB.login(
    (loginRes: any) => {
      if (!loginRes.authResponse) {
        console.log('User cancelled login or did not fully authorize.');
        return;
      }

      (async () => {
        const { accessToken } = loginRes.authResponse;

        window.FB.api('/me', { fields: 'id,name,email' }, async function (meRes: any) {
          try {
            const resLogin = await loginSelfService('THIRD_PARTY_META', meRes.id, accessToken);

            if (!resLogin.success) {
              console.error('Login failed', resLogin);
              switch (resLogin.errorCode) {
                case 1004:
                  goToSignup('meta', meRes.id, accessToken);
                  break;
                default:
                  console.error('FAIL');
              }

              return;
            } else handleLoginSuccess(resLogin.data);
          } catch (err) {
            console.error('Network error', err);
          }
        });
      })().catch((err) => console.error('key/encryption error', err));
    },
    { scope: 'email' }
  );
};

// 로그인 성공 시
const handleLoginSuccess = async (authToken) => {
  localStorage.setItem('authToken', authToken as string);
  sessionStorage.setItem('authToken', authToken as string);
  localStorage.setItem('loginType', 'SELF_SERVICE');

  // 임시로 넣은 값입니다. 추후 API로 받은 값을 바인딩할 수 있습니다.
  localStorage.setItem('userType', 'HOST');
  localStorage.setItem('initializedPassword', false);

  await adminStore.fetchPartnerHome();
  await userStore.fetchUserInfo();
  await movePage();

  localStorage.setItem('partner-code-admin', adminStore.getPartnerInfo.partnerCode);
};

const validateFormData = () => {
  const { partnerCode, userId, password } = inputFormLogin.value;

  if (!partnerCode.trim()) return 1015;
  if (!userId || !password) return 1002;

  return null;
};

const getLoginChannel = (): TypeLoginChannel => {
  const { redirectTo = '' } = route.query;

  if (redirectTo && redirectTo.includes('chat')) return 'CHAT';
  return 'SELF_SERVICE';
};

const movePage = () => {
  const { redirectTo = '' } = route.query;
  let path = '';

  if (redirectTo) {
    path = redirectTo.toString();
  } else {
    path = '/ss/dashboard';
  }

  router.push(path);
};

// 서브 로그인 처리
const handleLoginSub = async () => {
  const validationError = validateFormData();
  if (validationError) {
    handleErrorLogin({ errorCode: validationError } as IAppError);
    return;
  }

  isProcessingLogin.value = true;

  const { userId, partnerCode } = inputFormLogin.value;
  const loginChannel = getLoginChannel();

  try {
    const passwordEncrypted = await getEncryptedPassword();
    const { data, success } = await requestLoginApp(
      {
        userId,
        password: passwordEncrypted,
      },
      {
        type: 'SELF_SERVICE',
        partnerCode,
        loginChannel,
      }
    );

    if (success) {
      userStore.setLoginInfo({
        ...data,
        loginType: 'SELF_SERVICE',
        partnerCode,
      });

      await userStore.fetchUserInfo();
      movePage();
    }
  } catch (error) {
    handleErrorLogin(error as IAppError);
  } finally {
    isProcessingLogin.value = false;
  }
};

// 에러메시지
const handleErrorLogin = (responseError: IAppError) => {
  let messageDisplayed = '';

  switch (responseError.errorCode) {
    case 1001:
      messageDisplayed = 'invalid_access';
      break;
    case 1002:
    case 1004:
      messageDisplayed = 'failed_to_login_by_id_or_pw';
      break;
    case 1008:
      messageDisplayed = 'invalid_access';
      break;
    case 1015:
      messageDisplayed = 'login_no_such_partner_code';
      break;
    default:
      messageDisplayed = 'failed_to_login_for_unknown_reason';
      break;
  }
  errorLogin.value = { ...responseError, messageDisplayed };
};

// 회원가입 페이지로 이동
const goToSignup = (oAuth: string, userId: string, accessToken: string) => {
  localStorage.setItem('authToken', accessToken);
  sessionStorage.setItem('authToken', accessToken);
  router.push({
    name: 'ss-onboarding',
    query: {
      oAuth,
      userId,
      accessToken,
      referralCode: route.query.referralCode,
    },
  });
};

// 추가 사용자로 로그인
const setLoginTypeSub = () => (isSub.value = true);

// SNS로 로그인
const setLoginTypeHost = () => (isSub.value = false);

// 비밀번호 암호화
const getEncryptedPassword = async (): Promise<string> => {
  localStorage.removeItem('SC-USER-ID');
  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');

  const { data: { partnerPublicKey = '' } = { partnerPublicKey: '' } } = await requestAuthKey();
  return await encryptPassword(partnerPublicKey, inputFormLogin.value.password);
};

onMounted(() => {
  if (route.name === 'login-ss-sub') {
    setLoginTypeSub();
  }

  initFacebook();
});
</script>

<style scoped lang="scss">
.container-login {
  display: flex;
  width: 100%;
  min-height: 100dvh;
  height: 100dvh;

  @include respond-to(mobile-landscape) {
    flex-direction: column;
  }
}

.container-login-layout {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;

  @include respond-to(mobile-landscape) {
    min-height: calc(100dvh - 1rem);
  }
}

.login-left {
  background: var(--surface-background);

  @include respond-to(mobile-landscape) {
    display: none;
  }
}

.login-right {
  background-color: var(--surface-default);
  height: 100%;
  .container-login-detail {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  @include respond-to(mobile-landscape) {
    height: auto;
    overflow-y: auto;
    justify-content: space-between;
    .container-login-detail {
      padding: 4rem 0;
      flex: 1;
    }
    .wrapper-policy {
      flex: 0 0 auto;
    }
  }
}

.container-login-contents {
  display: flex;
  flex-direction: column;
  width: 20rem;
  gap: 1.5rem;
  align-items: center;
  text-align: center;
  margin-top: 0.5rem;
}

.wrapper-login-forms {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;

  .link {
    margin-top: 1rem;
  }
}

.wrapper-policy {
  display: flex;
  gap: 1rem;
  margin-top: auto;
  padding-bottom: 1rem;
}

.image {
  border-radius: 16px;
  background-color: var(--surface-default);
  height: 23rem;
  width: 100%;
}

:deep(.text-gradient) {
  display: inline-block;
  background: var(--font-gradient);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientShift 10s ease infinite;
  font-weight: inherit;
}
</style>
